import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Api, { formatUrl } from '@plone/volto/helpers/Api/Api';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { normalizePortalType } from 'volto-ministerio-publico-federal/actions/news';

interface Criterion {
  i: string;
  o: string;
  v?: unknown;
}

interface PreviewItem {
  '@id': string;
  title: string;
  '@type'?: string;
  effective?: string;
}

const PREVIEW_SIZE = 10;

const formatDate = (value?: string) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('pt-BR');
};

/**
 * Prévia dos itens que casam com os critérios da Collection.
 *
 * Ocupa o lugar do campo `customViewFields` ("Colunas da Tabela"), que só
 * tem efeito no layout `tabular_view` — layout que este portal não
 * registra (ver `config/views.ts`), então o campo aparecia no formulário
 * sem mudar absolutamente nada no site e só confundia o editor.
 *
 * No lugar dele, mostra o que o editor realmente quer conferir enquanto
 * mexe nos critérios: o título e a lista dos itens que a busca retorna
 * agora. Usa o mesmo endpoint que a listagem pública usa
 * (`@querystring-search`), então o resultado aqui é o resultado de lá.
 */
const CollectionPreviewWidget: React.FC<Record<string, any>> = () => {
  // Ignora `title`/`description` do schema de propósito: são os textos do
  // `customViewFields` ("Colunas da Tabela" / "...Visão de Tabela"), que
  // não descrevem mais o que este campo mostra.

  // O formulário de edição mantém os critérios já digitados aqui, então a
  // prévia acompanha o que está na tela, sem precisar salvar antes.
  const formData = useSelector((state: any) => state.form?.global || {});

  const query: Criterion[] = useMemo(
    () => (Array.isArray(formData?.query) ? formData.query : []),
    [formData?.query],
  );
  const sortOn = formData?.sort_on;
  const sortReversed = formData?.sort_reversed;

  const [items, setItems] = useState<PreviewItem[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryKey = JSON.stringify([query, sortOn, sortReversed]);

  useEffect(() => {
    if (!query.length) {
      setItems([]);
      setTotal(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const api = new Api();
    api
      .post(formatUrl('/@querystring-search'), {
        data: {
          // Mesma normalização do `queryByCollection` (actions/news): uma
          // Collection herdada da migração pede `portal_type: Noticia`, que
          // hoje tem zero itens, e a action acrescenta `News` antes de
          // consultar. Sem isso a prévia dizia "nenhum item" em 85 das 91
          // Collections do portal, enquanto a página mostrava centenas —
          // ou seja, mentia sobre o resultado real.
          query: normalizePortalType(query),
          b_size: PREVIEW_SIZE,
          b_start: 0,
          limit: 0,
          ...(sortOn ? { sort_on: sortOn } : {}),
          ...(sortOn
            ? { sort_order: sortReversed ? 'descending' : 'ascending' }
            : {}),
          metadata_fields: ['effective'],
        },
      })
      .then((res: any) => {
        if (cancelled) return;
        setItems(res?.items || []);
        setTotal(res?.items_total ?? 0);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Não foi possível carregar a prévia dos resultados.');
        setItems([]);
        setTotal(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [queryKey, query, sortOn, sortReversed]);

  return (
    <div className="field mpf-collection-preview mpf-collection-preview__container">
      <div className="mpf-collection-preview__label">Prévia dos resultados</div>

      {!query.length && (
        <p className="help">
          Adicione ao menos um critério acima para ver os itens que a listagem
          vai exibir.
        </p>
      )}

      {loading && <p className="help">Carregando prévia…</p>}

      {error && <p className="help">{error}</p>}

      {!loading && !error && query.length > 0 && (
        <>
          <p className="help">
            {total === 0
              ? 'Nenhum item corresponde aos critérios atuais.'
              : `${total} ${total === 1 ? 'item corresponde' : 'itens correspondem'} aos critérios atuais${
                  total && total > PREVIEW_SIZE
                    ? ` — mostrando os ${PREVIEW_SIZE} primeiros`
                    : ''
                }.`}
          </p>

          {items.length > 0 && (
            <ul className="mpf-collection-preview__list">
              {items.map((item) => {
                const date = formatDate(item.effective);
                return (
                  <li key={item['@id']}>
                    <a
                      href={flattenToAppURL(item['@id'])}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.title || item['@id']}
                    </a>
                    {date && <span className="help">{date}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <p className="help">
        Itens que a listagem exibe com os critérios acima. A prévia acompanha o
        que está na tela — não precisa salvar para conferir.
      </p>
    </div>
  );
};

export default CollectionPreviewWidget;
